import { ProductCategory, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { useLocalizeProperty } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCurrentService } from './useCurrentService';

export const useProductCategoryOptions = (updatedProductCategoryId?: string | null) => {
    const enterpriseId = useCurrentEnterpriseId();
    const { id } = useCurrentService();

    const localizeProperty = useLocalizeProperty();

    const query = useRepositorySuspenseQuery(
        'productCategories',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
            serviceIds: [id],
        },
        {
            select: ({ productCategories }) => {
                if (!productCategories) return [];

                const nestedCategoryIds = findNestedCategoryIds(productCategories, updatedProductCategoryId);
                const isChildren = (value: ProductCategory) => nestedCategoryIds.has(value.id);
                const isSelf = (value: ProductCategory) => value.id === updatedProductCategoryId;

                return productCategories
                    .filter((v) => !isSelf(v) && !isChildren(v))
                    .map((productCategory) =>
                        createOption({
                            label: localizeProperty(productCategory.names),
                            value: productCategory.id,
                        })
                    );
            },
        }
    );

    return query.data;
};

const findNestedCategoryIds = (
    productCategories: ProductCategory[],
    currentCategoryId?: string | null
): Set<string> => {
    if (!currentCategoryId) return new Set();

    const nestedCategoryIds = new Set<string>();
    const queue: string[] = [currentCategoryId];
    while (queue.length) {
        const parentId = queue.pop();
        const children = productCategories.filter((category) => category.parentCategoryId === parentId);
        for (const child of children) {
            if (!nestedCategoryIds.has(child.id)) {
                nestedCategoryIds.add(child.id);
                queue.push(child.id);
            }
        }
    }

    return nestedCategoryIds;
};
